import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function PagandoCaroSemPerceber() {
  const problemas = [
    { icon: 'ri-money-dollar-circle-line', texto: 'Você não sabe quanto está pagando de comissão' },
    { icon: 'ri-eye-off-line', texto: 'Não entende as coberturas reais' },
    { icon: 'ri-file-list-3-line', texto: 'Não consegue comparar propostas corretamente' },
    { icon: 'ri-question-mark', texto: 'E acaba decidindo no escuro' },
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: imgRef, isVisible: imgVisible } = useScrollAnimation();
  const { ref: listRef, visibleItems } = useStaggerAnimation(problemas.length);

  return (
   <section
  className="relative bg-amber-50/40 py-12 sm:py-16 lg:py-28 overflow-hidden"
  aria-labelledby="pagando-caro-heading"
>
      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
       <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[620px]">
          {/* ── Coluna de texto ── */}
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
           className={`flex flex-col justify-center lg:pr-8 transition-all duration-700 ease-out ${
  headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
}`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-300 px-3 sm:px-4 py-2 rounded-full mb-5 sm:mb-7 w-fit">
              <i className="ri-fire-line text-amber-600 text-sm" aria-hidden="true" />
              <span className="text-amber-700 font-bold text-xs uppercase tracking-widest">Método Validado</span>
            </div>

            {/* Título */}
            <h2
              id="pagando-caro-heading"
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.65rem] font-bold text-petrol-900 mb-5 sm:mb-6 leading-[1.2]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Você está pagando caro no seguro{' '}
              <span className="text-amber-600">— sem perceber</span>
            </h2>

            {/* Texto introdutório */}
            <p className="text-sm sm:text-base lg:text-lg text-petrol-700 leading-relaxed mb-2 sm:mb-3">
              A maioria dos segurados não entende o que contratou, paga mais do que deveria e só descobre os erros na hora do sinistro.
            </p>
            <p className="text-xs sm:text-sm lg:text-base text-petrol-600 leading-relaxed mb-5 sm:mb-6">
              Você provavelmente está cometendo estes erros agora:
            </p>

            {/* Lista de problemas */}
            <ul
              ref={listRef as React.RefObject<HTMLUListElement>}
              className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8"
              aria-label="Problemas comuns ao contratar seguro sem método"
            >
              {problemas.map((problema, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-2.5 sm:gap-3 bg-amber-50 border border-amber-100 rounded-lg sm:rounded-xl px-3 sm:px-4 py-3 sm:py-3.5 transition-all duration-500 ease-out ${
                    visibleItems[i] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                  }`}
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                    <i className={`${problema.icon} text-amber-600 text-sm sm:text-base`} aria-hidden="true" />
                  </div>
                  <span className="text-xs sm:text-sm lg:text-base text-petrol-900 font-semibold">{problema.texto}</span>
                </li>
              ))}
            </ul>

            {/* Alerta — barra escura */}
            <div className="bg-petrol-900 rounded-lg sm:rounded-xl px-4 sm:px-6 py-3.5 sm:py-4 mb-6 sm:mb-8 flex items-start gap-2.5 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-alert-line text-amber-400 text-base sm:text-lg" aria-hidden="true" />
              </div>
              <p className="text-xs sm:text-sm lg:text-base text-gray-200 leading-relaxed">
                Isso faz você <strong className="text-white">pagar mais caro</strong> e ainda ficar <strong className="text-white">mal protegido</strong>.
              </p>
            </div>

            {/* Bloco de autoridade IBX — premium */}
            <div className="relative bg-gradient-to-br from-strategic-green-50 via-white to-petrol-50 border border-strategic-green-200 rounded-xl sm:rounded-2xl p-5 sm:p-7 mb-5 sm:mb-7 shadow-md">
              <div className="absolute top-0 left-0 w-1 h-full bg-strategic-green-500 rounded-l-xl sm:rounded-l-2xl" aria-hidden="true" />
              <div className="flex items-center gap-2 mb-3 sm:mb-4 pl-2">
                <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-strategic-green-100 rounded-lg">
                  <i className="ri-building-2-line text-strategic-green-600 text-sm sm:text-base" aria-hidden="true" />
                </div>
                <span className="text-strategic-green-700 font-extrabold text-xs uppercase tracking-widest">IBX Brasil</span>
              </div>
              <p className="text-xs sm:text-sm lg:text-base text-petrol-800 leading-relaxed pl-2">
                Criamos um método prático que já ajudou mais de{' '}
                <strong className="text-petrol-900">4.800 segurados</strong> a{' '}
                <strong className="text-petrol-900">pagar menos</strong> e{' '}
                <strong className="text-petrol-900">contratar melhor</strong>.
              </p>
            </div>

            {/* Destaque final — fundo escuro */}
            <div className="bg-petrol-900 rounded-lg sm:rounded-xl px-4 sm:px-6 py-4 sm:py-5 flex items-start sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-action-orange-500/20 rounded-lg shrink-0">
                <i className="ri-double-quotes-l text-action-orange-400 text-lg sm:text-xl" aria-hidden="true" />
              </div>
              <p className="text-sm sm:text-base lg:text-lg font-semibold text-white italic leading-snug">
                "O problema não é o seguro. É a forma como ele é contratado."
              </p>
            </div>

            {/* CTA */}
            <div className="mt-6 sm:mt-8">
              <a
                href="#analise-gratuita"
                className="flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-action-orange-500 hover:bg-action-orange-600 active:scale-95 text-white font-bold text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto"
                aria-label="Aprender o método IBX por R$67"
              >
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0">
                  <i className="ri-book-open-line text-white text-base sm:text-lg" aria-hidden="true" />
                </div>
                <span className="whitespace-nowrap">Quero aprender o método por R$67</span>
                <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0">
                  <i className="ri-arrow-right-line text-white text-sm sm:text-base" aria-hidden="true" />
                </div>
              </a>
              <p className="text-xs text-petrol-500 mt-3 text-center sm:text-left">
                Sem risco. Acesso imediato.
              </p>
            </div>
          </div>

          {/* ── Coluna de imagem ── */}
          <div
            ref={imgRef as React.RefObject<HTMLDivElement>}
            className={`relative hidden lg:block h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[620px] mt-4 lg:mt-0 transition-all duration-700 ease-out delay-200 ${
              imgVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=young%20professional%20woman%20in%20modern%20office%20sitting%20at%20desk%20carefully%20reviewing%20insurance%20proposal%20documents%20with%20a%20confused%20and%20concerned%20expression%2C%20holding%20papers%20up%20to%20the%20light%2C%20soft%20warm%20ambient%20lighting%2C%20business%20casual%20attire%2C%20shallow%20depth%20of%20field%2C%20editorial%20photography%2C%20neutral%20beige%20and%20white%20tones%2C%20clean%20contemporary%20workspace%20background&width=900&height=700&seq=pagando-caro-img-v1&orientation=portrait"
                alt="Pessoa analisando proposta de seguro com expressão de dúvida em ambiente corporativo"
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
                width="900"
                height="700"
              />

              <div className="absolute inset-0 bg-gradient-to-r from-amber-50/20 via-transparent to-petrol-900/20" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-petrol-900/55 via-transparent to-transparent" aria-hidden="true" />

              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-5 shadow-2xl border border-white/60">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
                      <i className="ri-money-dollar-circle-fill text-amber-600 text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-[10px] text-amber-600 font-extrabold uppercase tracking-widest mb-1">Custo Invisível</p>
                      <p className="text-sm font-bold text-petrol-900 leading-snug">
                        Comissões e taxas ocultas que você nunca viu
                      </p>
                      <p className="text-xs text-petrol-500 mt-1">
                        Presentes na maioria das apólices contratadas sem método.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute top-6 right-6">
                <div className="bg-petrol-900/90 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl border border-white/10 text-center">
                  <p className="text-3xl font-extrabold text-amber-400 leading-none">4.800+</p>
                  <p className="text-[10px] text-gray-300 uppercase tracking-wide mt-1 leading-tight max-w-[90px]">
                    segurados já aprenderam o método IBX
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}